import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { HeartPulse, Activity, Gauge, Save, Bed, Brain, Dumbbell, Sliders } from "lucide-react";
import { PlayerAvatar } from "@/components/PlayerAvatar";
import { toast } from "sonner";

const RPE_LABELS = {
  1: "Muy suave", 2: "Suave", 3: "Suave-medio", 4: "Medio", 5: "Medio-alto",
  6: "Alto", 7: "Muy alto", 8: "Durísimo", 9: "Casi máximo", 10: "Máximo",
};
const RPE_COLORS = (rpe) => {
  if (rpe <= 3) return "bg-emerald-100 text-emerald-800";
  if (rpe <= 6) return "bg-amber-100 text-amber-800";
  if (rpe <= 8) return "bg-orange-100 text-orange-800";
  return "bg-rose-100 text-rose-800";
};
const TL_STYLES = {
  verde: "bg-emerald-100 text-emerald-700 border-emerald-300",
  amarillo: "bg-amber-100 text-amber-800 border-amber-300",
  rojo: "bg-rose-100 text-rose-700 border-rose-300",
};
const TL_LABEL = { verde: "VERDE", amarillo: "AMARILLO", rojo: "ROJO" };

const today = () => new Date().toISOString().slice(0, 10);

export default function LoadControl() {
  const { teamId: paramTeamId } = useParams();
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(paramTeamId || "");
  const [tab, setTab] = useState("cargas");
  const [thresholdsOpen, setThresholdsOpen] = useState(false);

  // Cargas (RPE) state
  const [rpeDate, setRpeDate] = useState(today());
  const [rpeDuration, setRpeDuration] = useState(75);
  const [rpeSessionType, setRpeSessionType] = useState("training");
  const [rpeMap, setRpeMap] = useState({}); // player_id -> rpe
  const [rpeEntries, setRpeEntries] = useState([]);
  const [savingRpe, setSavingRpe] = useState(false);

  // Wellness state
  const [wellDate, setWellDate] = useState(today());
  const [wellMap, setWellMap] = useState({}); // player_id -> {sleep, fatigue, stress, soreness}
  const [wellEntries, setWellEntries] = useState([]);
  const [savingWell, setSavingWell] = useState(false);

  // Summary
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get("/teams")
      .then((r) => {
        setTeams(r.data || []);
        if (!paramTeamId && r.data?.length > 0) setSelectedTeamId(r.data[0].id);
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, [paramTeamId]);

  // Reload everything when team changes
  useEffect(() => {
    if (!selectedTeamId) return;
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeamId]);

  const refreshAll = async () => {
    try {
      const [pl, rpes, wells, sum] = await Promise.all([
        api.get(`/players?team_id=${selectedTeamId}`),
        api.get(`/rpe-entries?team_id=${selectedTeamId}`),
        api.get(`/wellness-entries?team_id=${selectedTeamId}`),
        api.get(`/load-control/summary?team_id=${selectedTeamId}`),
      ]);
      setPlayers((pl.data || []).sort((a, b) => (a.dorsal || 99) - (b.dorsal || 99)));
      setRpeEntries(rpes.data || []);
      setWellEntries(wells.data || []);
      setSummary(sum.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const team = teams.find((t) => t.id === selectedTeamId);

  // Pre-fill RPE map from existing entries for the selected date
  useEffect(() => {
    const map = {};
    rpeEntries.forEach((e) => {
      if (e.date === rpeDate && e.session_type === rpeSessionType) {
        map[e.player_id] = e.rpe;
      }
    });
    setRpeMap(map);
  }, [rpeEntries, rpeDate, rpeSessionType]);

  // Pre-fill Wellness map for selected date
  useEffect(() => {
    const map = {};
    wellEntries.forEach((e) => {
      if (e.date === wellDate) {
        map[e.player_id] = {
          sleep: e.sleep, fatigue: e.fatigue, stress: e.stress, soreness: e.soreness,
        };
      }
    });
    setWellMap(map);
  }, [wellEntries, wellDate]);

  const saveRpeBulk = async () => {
    const entries = Object.entries(rpeMap)
      .filter(([, v]) => v && v >= 1 && v <= 10)
      .map(([player_id, rpe]) => ({ player_id, rpe }));
    if (entries.length === 0) {
      toast.warning("Indica el RPE de al menos un jugador");
      return;
    }
    setSavingRpe(true);
    try {
      await api.post("/rpe-entries/bulk", {
        team_id: selectedTeamId,
        date: rpeDate,
        session_type: rpeSessionType,
        duration_min: rpeDuration,
        entries,
      });
      toast.success(`RPE guardado para ${entries.length} jugadores`);
      await refreshAll();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingRpe(false);
    }
  };

  const saveWellnessBulk = async () => {
    const entries = Object.entries(wellMap)
      .filter(([, v]) => v && v.sleep && v.fatigue && v.stress && v.soreness)
      .map(([player_id, v]) => ({
        player_id,
        sleep: v.sleep, fatigue: v.fatigue, stress: v.stress, soreness: v.soreness,
      }));
    if (entries.length === 0) {
      toast.warning("Completa al menos un jugador con los 4 indicadores");
      return;
    }
    setSavingWell(true);
    try {
      await api.post("/wellness-entries/bulk", {
        team_id: selectedTeamId,
        date: wellDate,
        entries,
      });
      toast.success(`Wellness guardado para ${entries.length} jugadores`);
      await refreshAll();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingWell(false);
    }
  };

  // Weekly load per player (sum of last 7 days)
  const weeklyByPlayer = useMemo(() => {
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 7);
    const cs = cutoff.toISOString().slice(0, 10);
    const out = {};
    rpeEntries.forEach((e) => {
      if (e.date >= cs) {
        out[e.player_id] = (out[e.player_id] || 0) + (e.internal_load || 0);
      }
    });
    return out;
  }, [rpeEntries]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-slate-900" style={{ fontFamily: "Nunito" }}>
            Cargas & Wellness
          </h1>
          <p className="text-sm text-slate-500">
            RPE post-sesión · Wellness diario · Semáforo EFC
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Equipo</Label>
            <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
              <SelectTrigger className="min-w-[240px]" data-testid="loadctl-team-select">
                <SelectValue placeholder="Selecciona equipo" />
              </SelectTrigger>
              <SelectContent>
                {teams.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name} <span className="text-xs text-slate-400">{t.category}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {user?.role === "admin" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setThresholdsOpen(true)}
              data-testid="open-wellness-thresholds"
              title="Editar umbrales del semáforo por categoría"
            >
              <Sliders className="h-4 w-4 mr-1.5" /> Umbrales
            </Button>
          )}
        </div>
      </div>

      {!team ? (
        <Card><CardContent className="py-10 text-sm text-slate-400 italic text-center">
          Selecciona un equipo para empezar.
        </CardContent></Card>
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="cargas" data-testid="tab-cargas">
              <Activity className="h-4 w-4 mr-1.5" /> Cargas (RPE)
            </TabsTrigger>
            <TabsTrigger value="wellness" data-testid="tab-wellness">
              <HeartPulse className="h-4 w-4 mr-1.5" /> Wellness
            </TabsTrigger>
            <TabsTrigger value="semaforo" data-testid="tab-semaforo">
              <Gauge className="h-4 w-4 mr-1.5" /> Semáforo
            </TabsTrigger>
          </TabsList>

          <TabsContent value="cargas">
            <RpeTab
              players={players}
              date={rpeDate} setDate={setRpeDate}
              duration={rpeDuration} setDuration={setRpeDuration}
              sessionType={rpeSessionType} setSessionType={setRpeSessionType}
              rpeMap={rpeMap} setRpeMap={setRpeMap}
              weeklyByPlayer={weeklyByPlayer}
              saving={savingRpe}
              onSave={saveRpeBulk}
            />
          </TabsContent>

          <TabsContent value="wellness">
            <WellnessTab
              players={players}
              date={wellDate} setDate={setWellDate}
              wellMap={wellMap} setWellMap={setWellMap}
              entries={wellEntries}
              saving={savingWell}
              onSave={saveWellnessBulk}
            />
          </TabsContent>

          <TabsContent value="semaforo">
            <SemaforoTab summary={summary} />
          </TabsContent>
        </Tabs>
      )}

      {thresholdsOpen && (
        <WellnessThresholdsEditor onClose={() => setThresholdsOpen(false)} onSaved={() => refreshAll()} />
      )}
    </div>
  );
}

// -------------------- Tab: Cargas (RPE) --------------------
function RpeTab({
  players, date, setDate, duration, setDuration,
  sessionType, setSessionType, rpeMap, setRpeMap,
  weeklyByPlayer, saving, onSave,
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Activity className="h-4 w-4 text-purple-600" />
          RPE post-sesión (Borg CR-10)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Fecha sesión</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="rpe-date" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Tipo</Label>
            <Select value={sessionType} onValueChange={setSessionType}>
              <SelectTrigger data-testid="rpe-session-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="training">Entrenamiento</SelectItem>
                <SelectItem value="match">Partido</SelectItem>
                <SelectItem value="other">Otro</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Duración (min)</Label>
            <Input type="number" min={1} max={300} value={duration}
              onChange={(e) => setDuration(Number(e.target.value) || 0)}
              data-testid="rpe-duration" />
          </div>
          <div className="flex items-end">
            <Button
              onClick={onSave}
              disabled={saving}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="save-rpe"
            >
              <Save className="h-4 w-4 mr-1.5" /> {saving ? "Guardando…" : "Guardar RPE"}
            </Button>
          </div>
        </div>

        <div className="text-xs text-slate-500 mb-2">
          Pulsa un número del <b>1 al 10</b> para cada jugador. Carga interna = RPE × Duración.
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
              <tr>
                <th className="px-2 py-2 text-left font-bold w-44">Jugador</th>
                <th className="px-2 py-2 text-center font-bold">RPE</th>
                <th className="px-2 py-2 text-right font-bold w-32">Carga sesión</th>
                <th className="px-2 py-2 text-right font-bold w-32">Carga 7d</th>
              </tr>
            </thead>
            <tbody>
              {players.length === 0 && (
                <tr><td colSpan={4} className="py-6 text-center text-slate-400 italic">
                  Sin jugadores en este equipo.
                </td></tr>
              )}
              {players.map((p) => {
                const rpe = rpeMap[p.id] || 0;
                const sessionLoad = rpe * (duration || 0);
                return (
                  <tr key={p.id} className="border-b border-slate-100">
                    <td className="px-2 py-2 text-slate-800 font-semibold">
                      <div className="flex items-center gap-2">
                        <PlayerAvatar player={p} className="w-7 h-7" />
                        <span>{p.name}</span>
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex flex-wrap justify-center gap-1">
                        {[1,2,3,4,5,6,7,8,9,10].map((n) => (
                          <button
                            key={n}
                            type="button"
                            onClick={() => setRpeMap({ ...rpeMap, [p.id]: rpe === n ? 0 : n })}
                            className={`w-7 h-7 rounded-md text-xs font-bold border transition ${
                              rpe === n
                                ? `${RPE_COLORS(n)} border-current ring-2 ring-current/40`
                                : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                            }`}
                            data-testid={`rpe-${p.id}-${n}`}
                            title={RPE_LABELS[n]}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                    </td>
                    <td className="px-2 py-2 text-right font-bold text-slate-700">
                      {sessionLoad > 0 ? `${sessionLoad} AU` : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-2 py-2 text-right text-slate-600">
                      {(weeklyByPlayer[p.id] || 0) > 0
                        ? `${weeklyByPlayer[p.id]} AU`
                        : <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// -------------------- Tab: Wellness --------------------
const W_DIMS = [
  { k: "sleep", label: "Sueño", icon: Bed, hint: "1 muy mal · 5 excelente" },
  { k: "fatigue", label: "Fatiga", icon: Activity, hint: "1 muy fatigado · 5 fresco" },
  { k: "stress", label: "Estrés", icon: Brain, hint: "1 muy estresado · 5 relajado" },
  { k: "soreness", label: "Dolor muscular", icon: Dumbbell, hint: "1 muy dolorido · 5 sin dolor" },
];

function WellnessTab({ players, date, setDate, wellMap, setWellMap, saving, onSave }) {
  const setVal = (pid, dim, val) => {
    setWellMap({
      ...wellMap,
      [pid]: { ...(wellMap[pid] || {}), [dim]: val },
    });
  };
  const scoreOf = (pid) => {
    const v = wellMap[pid] || {};
    if (!v.sleep || !v.fatigue || !v.stress || !v.soreness) return null;
    return v.sleep + v.fatigue + v.stress + v.soreness;
  };
  const tlOf = (score) => score == null
    ? null : score >= 16 ? "verde" : score >= 11 ? "amarillo" : "rojo";

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <HeartPulse className="h-4 w-4 text-purple-600" />
          Cuestionario de bienestar (Wellness)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
          <div className="sm:col-span-2">
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Fecha</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="well-date" />
          </div>
          <div className="sm:col-span-2 flex items-end">
            <Button
              onClick={onSave}
              disabled={saving}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="save-wellness"
            >
              <Save className="h-4 w-4 mr-1.5" /> {saving ? "Guardando…" : "Guardar Wellness"}
            </Button>
          </div>
        </div>

        <div className="text-xs text-slate-500 mb-2">
          Cada dimensión va de <b>1</b> (peor) a <b>5</b> (mejor). Total 4-20: ≥16 verde, 11-15 amarillo, ≤10 rojo.
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
              <tr>
                <th className="px-2 py-2 text-left font-bold w-44">Jugador</th>
                {W_DIMS.map((d) => (
                  <th key={d.k} className="px-2 py-2 text-center font-bold" title={d.hint}>
                    <d.icon className="h-3 w-3 inline mr-0.5" />{d.label}
                  </th>
                ))}
                <th className="px-2 py-2 text-center font-bold w-24">Total</th>
                <th className="px-2 py-2 text-center font-bold w-24">Estado</th>
              </tr>
            </thead>
            <tbody>
              {players.length === 0 && (
                <tr><td colSpan={7} className="py-6 text-center text-slate-400 italic">
                  Sin jugadores.
                </td></tr>
              )}
              {players.map((p) => {
                const score = scoreOf(p.id);
                const tl = tlOf(score);
                return (
                  <tr key={p.id} className="border-b border-slate-100">
                    <td className="px-2 py-2 text-slate-800 font-semibold">
                      <div className="flex items-center gap-2">
                        <PlayerAvatar player={p} className="w-7 h-7" />
                        <span>{p.name}</span>
                      </div>
                    </td>
                    {W_DIMS.map((d) => {
                      const v = wellMap[p.id]?.[d.k] || 0;
                      return (
                        <td key={d.k} className="px-2 py-2">
                          <div className="flex justify-center gap-1">
                            {[1,2,3,4,5].map((n) => (
                              <button
                                key={n}
                                type="button"
                                onClick={() => setVal(p.id, d.k, v === n ? 0 : n)}
                                className={`w-6 h-6 rounded text-[10px] font-bold border ${
                                  v === n
                                    ? "bg-purple-600 text-white border-purple-700"
                                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                                }`}
                                data-testid={`well-${p.id}-${d.k}-${n}`}
                              >{n}</button>
                            ))}
                          </div>
                        </td>
                      );
                    })}
                    <td className="px-2 py-2 text-center font-bold">
                      {score ?? <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {tl ? (
                        <span className={`text-[10px] font-bold px-2 py-1 rounded border ${TL_STYLES[tl]}`}>
                          {TL_LABEL[tl]}
                        </span>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// -------------------- Tab: Semáforo --------------------
function SemaforoTab({ summary }) {
  if (!summary) {
    return <Card><CardContent className="py-10 text-center text-slate-400 italic">Cargando…</CardContent></Card>;
  }
  const players = summary.players || [];

  const counts = players.reduce((acc, p) => {
    const tl = p.latest_wellness?.traffic_light;
    if (tl) acc[tl] = (acc[tl] || 0) + 1;
    else acc.sin = (acc.sin || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard label="VERDE" value={counts.verde || 0} tone="verde" />
        <KpiCard label="AMARILLO" value={counts.amarillo || 0} tone="amarillo" />
        <KpiCard label="ROJO" value={counts.rojo || 0} tone="rojo" />
        <KpiCard label="Sin datos" value={counts.sin || 0} tone="gris" />
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Gauge className="h-4 w-4 text-purple-600" /> Semáforo del equipo
          </CardTitle>
          <div className="text-xs text-slate-500">
            Ventana: {summary.date_from} → {summary.date_to} · Semana: desde {summary.week_from}
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
              <tr>
                <th className="px-2 py-2 text-left font-bold">Jugador</th>
                <th className="px-2 py-2 text-right font-bold">Carga semanal</th>
                <th className="px-2 py-2 text-right font-bold">Sesiones</th>
                <th className="px-2 py-2 text-right font-bold">Carga 28d</th>
                <th className="px-2 py-2 text-right font-bold">ACWR</th>
                <th className="px-2 py-2 text-right font-bold">Wellness</th>
                <th className="px-2 py-2 text-center font-bold">Estado</th>
              </tr>
            </thead>
            <tbody>
              {players.length === 0 && (
                <tr><td colSpan={7} className="py-6 text-center text-slate-400 italic">
                  Sin jugadores.
                </td></tr>
              )}
              {players.map((p) => {
                const tl = p.latest_wellness?.traffic_light;
                const acwr = p.acwr;
                const acwrStyle = acwr == null
                  ? "text-slate-400"
                  : acwr >= 0.8 && acwr <= 1.3 ? "text-emerald-700 font-bold"
                  : acwr > 1.5 || acwr < 0.5 ? "text-rose-700 font-bold"
                  : "text-amber-700 font-bold";
                return (
                  <tr key={p.player_id} className="border-b border-slate-100 hover:bg-slate-50/40">
                    <td className="px-2 py-2 text-slate-800 font-semibold">
                      <div className="flex items-center gap-2">
                        <PlayerAvatar player={players.find((x) => x.id === p.player_id) || { dorsal: p.dorsal }} className="w-7 h-7" />
                        <span>{p.player_name}</span>
                      </div>
                    </td>
                    <td className="px-2 py-2 text-right">{p.weekly_load} AU</td>
                    <td className="px-2 py-2 text-right text-slate-600">{p.weekly_sessions}</td>
                    <td className="px-2 py-2 text-right text-slate-600">{p.monthly_load} AU</td>
                    <td className={`px-2 py-2 text-right ${acwrStyle}`}>
                      {acwr != null ? acwr.toFixed(2) : "—"}
                    </td>
                    <td className="px-2 py-2 text-right text-slate-600">
                      {p.latest_wellness ? `${p.latest_wellness.score}/20` : "—"}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {tl ? (
                        <span className={`text-[10px] font-bold px-2 py-1 rounded border ${TL_STYLES[tl]}`}>
                          {TL_LABEL[tl]}
                        </span>
                      ) : <span className="text-slate-300 text-xs italic">sin datos</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({ label, value, tone }) {
  const toneClass = {
    verde: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amarillo: "bg-amber-50 border-amber-200 text-amber-800",
    rojo: "bg-rose-50 border-rose-200 text-rose-800",
    gris: "bg-slate-50 border-slate-200 text-slate-700",
  }[tone];
  return (
    <div className={`border rounded-lg p-3 ${toneClass}`}>
      <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">{label}</div>
      <div className="text-3xl font-black">{value}</div>
    </div>
  );
}


// -------------------- Wellness Thresholds Editor (admin) --------------------
const CATEGORIES = ["prebenjamin", "benjamin", "alevin", "infantil", "cadete", "juvenil", "aficionado"];

function WellnessThresholdsEditor({ onClose, onSaved }) {
  const [thresholds, setThresholds] = useState({});
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    api.get("/wellness-thresholds")
      .then((r) => setThresholds(r.data || {}))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const update = (cat, key, val) => {
    setThresholds((t) => ({
      ...t,
      [cat]: { ...(t[cat] || { verde: 16, amarillo: 11 }), [key]: Number(val) || 0 },
    }));
  };

  const saveOne = async (cat) => {
    const v = thresholds[cat];
    if (!v) return;
    if (v.amarillo >= v.verde) {
      toast.error("'amarillo' debe ser menor que 'verde'");
      return;
    }
    setSaving(cat);
    try {
      await api.put(`/wellness-thresholds/${cat}`, { verde: v.verde, amarillo: v.amarillo });
      toast.success(`Umbrales de ${cat.toUpperCase()} guardados`);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(null);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Umbrales del Semáforo Wellness</DialogTitle>
          <DialogDescription className="text-xs">
            Score total = sueño + fatiga + estrés + dolor (cada uno 1-5, máx 20).
            Por encima del umbral <b>verde</b> → estado óptimo. Entre amarillo y verde → vigilar.
            Por debajo de <b>amarillo</b> → rojo (modificar/abortar sesión).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {CATEGORIES.map((cat) => {
            const v = thresholds[cat] || { verde: 16, amarillo: 11 };
            return (
              <div key={cat} className="border border-slate-200 rounded p-3 flex flex-wrap items-end gap-3">
                <div className="font-bold text-slate-700 min-w-[110px]">{cat.toUpperCase()}</div>
                <div>
                  <Label className="text-[10px] text-emerald-700 font-bold">VERDE ≥</Label>
                  <Input
                    type="number" min={4} max={20}
                    value={v.verde}
                    onChange={(e) => update(cat, "verde", e.target.value)}
                    className="w-20"
                    data-testid={`th-${cat}-verde`}
                  />
                </div>
                <div>
                  <Label className="text-[10px] text-amber-700 font-bold">AMARILLO ≥</Label>
                  <Input
                    type="number" min={4} max={20}
                    value={v.amarillo}
                    onChange={(e) => update(cat, "amarillo", e.target.value)}
                    className="w-20"
                    data-testid={`th-${cat}-amarillo`}
                  />
                </div>
                <div className="text-[11px] text-slate-500 flex-1">
                  Rojo: &lt; {v.amarillo}
                </div>
                <Button
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={() => saveOne(cat)}
                  disabled={saving === cat}
                  data-testid={`save-th-${cat}`}
                >
                  <Save className="h-3 w-3 mr-1" /> {saving === cat ? "…" : "Guardar"}
                </Button>
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
